import React, { useState, useEffect, FC } from 'react';
import classnames from 'classnames';
import makeStyles from '@material-ui/styles/makeStyles';
import { Button, Typography } from '@material-ui/core';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import AppBar from '../components/AppBar/AppBar';
import { getPlaceHolderHacks, tan6 } from '../utils/utils';
import HeroContainer from '../components/HeroContainer/HeroContainer';
import useCriteoScript from '../hooks/useCriteoScript';
import AwardsContainer from '../components/AwardsContainer/AwardsContainer';
import { getHacks } from '../utils/api';
import HackCarousel from '../components/HackCarousel/HackCarousel';
import FeaturesListMobile from '../components/FeaturesList/FeaturesListMobile';
import FeaturesList from '../components/FeaturesList/FeaturesList';
import PhoneCarousel from '../components/PhoneCarousel/PhoneCarousel';
import DownloadComponent from '../components/DownloadComponent/DownloadComponent';
import Footer from '../components/Footer/Footer';
import SEOHelmet from '../helmet/SEOHelmet';
import { withPrismicPreview } from '@zachsz/gatsby-plugin-prismic-previews';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import IframeResizer from 'iframe-resizer-react';

const useStyles = makeStyles((theme: any): any => {
  return {
    root: {
      width: '100%',
      backgroundColor: '#F8F8F8',
    },
    heroTextContainer: {
      whiteSpace: 'pre-wrap',
      [theme.breakpoints.up(1200)]: {
        width: '43%',
      },
    },
    heroImageContainer: {
      position: 'relative',
      width: '100%',
      maxWidth: '327px',
      margin: '0 auto 32px',
      height: '285px',
      [theme.breakpoints.up(1200)]: {
        maxWidth: '572px',
        height: '496px',
      },
    },
    heroImage: {
      display: 'block',
      position: 'absolute',
      top: '12px',
      width: '251px',
      height: '244px',
      [theme.breakpoints.up(1200)]: {
        top: '19px',
        width: '438px',
        height: '426px',
      },
    },
    heroImagePhone: {
      display: 'block',
      position: 'absolute !important',
      right: 0,
      top: 0,
      width: '134px',
      height: '285px',
      filter: 'drop-shadow(-10px 10px 30px rgba(0, 0, 0, 0.1))',
      border: '2px solid #fff',
      borderRadius: '20px',
      [theme.breakpoints.up(1200)]: {
        width: '234px',
        height: '496px',
        border: '4px solid #fff',
      },
    },
    heroDownloadButtons: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginBottom: 24,
      '& a': {
        '&:first-child': {
          marginRight: 12,
        },
      },
      '& img': {
        display: 'block',
        height: 41,
      },
      [theme.breakpoints.up(1200)]: {
        marginBottom: 30,
        '& a': {
          '& img': {
            height: 63,
          },
        },
      },
    },
    sectionContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 25px',
      maxWidth: 1440,
      margin: '0 auto',
      '& h2': {
        color: theme.palette.primary.main,
        fontSize: 22,
        lineHeight: '26.07px',
        fontWeight: 700,
        letterSpacing: -0.5,
        textAlign: 'center',
        marginBottom: 20,
      },
      [theme.breakpoints.up(1200)]: {
        padding: '0',
        maxWidth: 1170,
        margin: '0 auto',
        '& h2': {
          fontSize: 32,
          lineHeight: '37.92px',
          marginBottom: theme.spacing(6),
        },
      },
    },
    hackCarouselContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: '600px',
      marginTop: '32px',
      [theme.breakpoints.up(1200)]: {
        maxWidth: '1170px',
        marginTop: '48px',
      },
    },
    hackCarouselTitleContainer: {
      '& h2': {
        fontSize: '24px',
        lineHeight: '30px',
        textAlign: 'left',
        marginBottom: '32px',
      },
      [theme.breakpoints.up(1200)]: {
        '& h2': {
          fontSize: 34,
          lineHeight: '40px',
          textAlign: 'center',
          maxWidth: '756px',
          marginBottom: '64px',
        },
      },
    },
    downloadButton: {
      borderRadius: '0 8px 8px',
      padding: '12px 22px',
      fontSize: '17px',
      lineHeight: '24px',
      margin: '32px 0',
      letterSpacing: '0',
    },
    sectionNavy: {
      backgroundColor: theme.palette.primary.main,
      transform: 'skewY(-6.733deg)',
      marginTop: `calc((100vw * ${tan6} * -1) / 2)`,
      marginBottom: '32px',
      padding: `calc((100vw * ${tan6} * -1) / 2) 0`,
      borderRadius: '25px 50px 25px 50px',
      [theme.breakpoints.up(1170)]: {
        marginBottom: `calc((1170px * ${tan6} * -1) / 2)`,
        marginTop: `calc((1170px * ${tan6} * -1) / 2)`,
        padding: `calc((1170px * ${tan6} * -1) / 2)`,
      },
    },
    sectionNavyContent: {
      transform: 'skewY(6.733deg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '108px 25px 95px',
      maxWidth: '600px',
      margin: '0 auto',
      [theme.breakpoints.up(1200)]: {
        maxWidth: '1170px',
        padding: '76px 0 136px',
      },
    },
    carouselContainerOuter: {
      overflowX: 'hidden',
      position: 'relative',
      '&:before': {
        content: JSON.stringify(''),
        position: 'absolute',
        left: '0',
        top: '78.06px',
        zIndex: '1',
        pointerEvents: 'none',
        width: '15vw',
        height: '453px',
        backgroundImage: 'linear-gradient(to left, rgba(248, 248, 248, 0), rgba(248, 248, 248, 1) 90%)',
      },
      '&:after': {
        content: JSON.stringify(''),
        position: 'absolute',
        right: '0',
        top: '78.06px',
        zIndex: '1',
        pointerEvents: 'none',
        width: '15vw',
        height: '453px',
        backgroundImage: 'linear-gradient(to right, rgba(248, 248, 248, 0), rgba(248, 248, 248, 1) 90%)',
      },
      [theme.breakpoints.up(1200)]: {
        '&:before': {
          height: '530px',
          top: '134px',
          width: '20vw',
        },
        '&:after': {
          height: '530px',
          top: '134px',
          width: '20vw',
        },
      },
    },
    phoneCarouselContainer: {
      display: 'block',
      paddingTop: 32,
      marginBottom: '32px',
      [theme.breakpoints.up(1200)]: {
        paddingTop: '48px',
        marginBottom: '64px',
      },
    },
    bottomSectionDownloadButtons: {
      '& a': {
        display: 'block',
      },
      '& img': {
        marginBottom: 25,
        height: 50,
      },
      [theme.breakpoints.up(600)]: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        '& a': {
          marginRight: 20,
          '&:last-child': {
            marginRight: 0,
          },
        },
      },
    },
  };
});

const placeholderHacks = getPlaceHolderHacks();

const Index: FC<any> = ({ data, location, pageContext }) => {
  const classes = useStyles() as any;
  const { language } = useI18next();
  const { t } = useTranslation();
  const [hacks, setHacks] = useState(placeholderHacks);
  const [filter, setFilter] = useState(null);
  const { criteoPageView, trackCriteoButtonClick } = useCriteoScript();
  const node = data?.prismicLandingV3;
  const heroImageLeft = getImage(node?.data.hero_image_left);
  const heroImageRight = getImage(node?.data.hero_image_right);

  useEffect(() => {
    getHacks({ filter, setHacks, placeholderHacks, language: language.split('-')[0] });
  }, [setHacks, filter]);
  return (
    <>
      <SEOHelmet
        title={node?.data?.meta_title.text}
        description={node?.data?.meta_description.text}
        keywords={node?.data?.meta_keywords}
        location={location}
        image={node?.data?.meta_image}
        schemaKey={node?.id}
        locale={pageContext?.language}
      />
      <AppBar />
      <main className={classes.root}>
        <HeroContainer>
          <div className={classes.heroTextContainer}>
            <Typography variant="h1">{t('landingPage.title')}</Typography>
            <Typography variant="subtitle1">{t('landingPage.subtitle')}</Typography>
            <div className={classes.heroDownloadButtons}>
              <a
                onClick={trackCriteoButtonClick}
                href="https://uptime.app.link/S5iL3GrK0cb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/home-page/apple-button.svg" alt="" />
              </a>
              <a
                onClick={trackCriteoButtonClick}
                href="https://uptime.app.link/S5iL3GrK0cb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/home-page/google-button.svg" alt="" />
              </a>
            </div>
            <AwardsContainer />
          </div>
          <div className={classes.heroImageContainer}>
            <GatsbyImage
              className={classes.heroImage}
              image={heroImageLeft as IGatsbyImageData}
              alt={node?.data.hero_image_left.alt || ''}
            />
            <GatsbyImage
              className={classes.heroImagePhone}
              image={heroImageRight as IGatsbyImageData}
              alt={node?.data.hero_image_right.alt || ''}
            />
          </div>
        </HeroContainer>
        <div className={classes.section}>
          <div className={classes.sectionContent}>
            <div className={classes.hackCarouselContainer}>
              <div className={classes.hackCarouselTitleContainer}>
                <Typography variant="h2">{node?.data.library_title.text}</Typography>
              </div>
              <HackCarousel hacks={hacks} filter={filter} setFilter={setFilter} />
              <div style={{ width: '100vw', marginTop: '32px' }}>
                <IframeResizer
                  src="https://widget.senja.io/widget/24e6ebdf-f2ae-4045-bc7e-81bf85d1728c"
                  style={{ width: '1px', minWidth: '100%', border: 0 }}
                />
              </div>
              <Button
                className={classes.downloadButton}
                variant="contained"
                color="secondary"
                href="https://to.uptime.app/S5iL3GrK0cb"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('landingPage.downloadCTA')}
              </Button>
            </div>
          </div>
        </div>
        <div className={classes.sectionNavy}>
          <div className={classes.sectionNavyContent}>
            <FeaturesListMobile items={node?.data.app_highlights} />
            <FeaturesList items={node?.data.app_highlights} />
          </div>
        </div>
        <div className={classes.carouselContainerOuter}>
          <div className={classnames(classes.sectionContent, classes.phoneCarouselContainer)}>
            <Typography variant="h2">{node?.data?.screenshots_title.text}</Typography>
            <PhoneCarousel items={node?.data?.screenshot_carousel} />
          </div>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionContent}>
            <Typography variant="h2">{node?.data?.testimonials_title.text}</Typography>
            <div style={{ width: '100%', marginBottom: '32px' }}>
              <IframeResizer
                src="https://widget.senja.io/widget/32f4d658-1e72-47f2-bf44-1fb741a217bd"
                style={{ width: '1px', minWidth: '100%', border: 0 }}
              />
            </div>
          </div>
        </div>
        <DownloadComponent showPhones />
      </main>
      <Footer />
    </>
  );
};

export default withPrismicPreview(Index);

export const query = graphql`
  query LandingPageQueryEn($id: String, $language: String) {
    prismicLandingV3(id: { eq: $id }) {
      _previewable
      id
      prismicId
      type
      lang
      first_publication_date
      last_publication_date
      data {
        hero_title {
          text
        }
        hero_text {
          text
        }
        hero_image_left {
          alt
          gatsbyImageData
        }
        hero_image_right {
          alt
          gatsbyImageData(width: 226, imgixParams: { lossless: true })
        }
        library_title {
          text
        }
        app_highlights {
          title {
            text
          }
          text {
            text
          }
          image {
            alt
            gatsbyImageData(width: 454, imgixParams: { lossless: true })
          }
        }
        screenshots_title {
          text
        }
        screenshot_carousel {
          text {
            text
          }
          title {
            text
          }
          image {
            alt
            gatsbyImageData(width: 245, imgixParams: { lossless: true })
          }
        }
        title {
          text
        }
        subtitle {
          text
        }
        testimonials_title {
          text
        }
        meta_title {
          text
        }
        meta_description {
          text
        }
        meta_keywords {
          meta_keyword {
            text
          }
        }
        meta_image {
          alt
          url
          dimensions {
            width
            height
          }
        }
      }
    }
    locales: allLocale(filter: {language: {in: ["en-US", $language]}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
